import React from 'react';
import { motion } from 'framer-motion';
import { Award, Trophy, Flame, Target, Star, Crown, Shield } from 'lucide-react';
import { Achievement } from '../../../types/api.types';
import { wrappedTranslations } from '../../../translations/wrapped';

// Map achievement icon strings to actual icon components
const achievementIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
    trophy: Trophy,
    flame: Flame,
    target: Target,
    star: Star,
    crown: Crown,
    shield: Shield,
};

interface AchievementsCardProps {
    achievements: Achievement[];
    lang: 'en' | 'fr' | 'de' | 'it';
}

const AchievementsCard: React.FC<AchievementsCardProps> = ({ achievements, lang }) => {
    const t = wrappedTranslations[lang];
    // Take top 3 achievements
    const topAchievements = achievements.slice(0, 3);

    return (
        <div className="h-full w-full flex flex-col items-center justify-center text-center p-6">
            <div className="max-w-4xl w-full flex flex-col items-center">
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                >
                    <Award className="w-20 h-20 md:w-32 md:h-32 mb-8 mx-auto text-[#1C7AD6]" />
                </motion.div>

                <motion.h2
                    className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 tracking-tight"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    {t.hallOfFame}
                </motion.h2>

                <motion.p
                    className="text-base md:text-lg text-gray-600 mb-12"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                >
                    {t.yourMilestones}
                </motion.p>

                <div className="w-full max-w-3xl space-y-6">
                    {topAchievements.map((achievement, index) => (
                        <motion.div
                            key={index}
                            className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 flex items-center gap-6 md:gap-8 text-left shadow-lg hover:shadow-xl transition-shadow"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.6 + (index * 0.1) }}
                        >
                            <div className="bg-gray-100 p-4 md:p-5 rounded-full shrink-0">
                                {(() => {
                                    const IconComponent = achievementIconMap[achievement.icon] || Trophy;
                                    return <IconComponent className="w-10 h-10 md:w-14 md:h-14 text-[#1C7AD6]" />;
                                })()}
                            </div>
                            <div>
                                <div className="font-bold text-gray-900 text-xl md:text-2xl mb-1 md:mb-2">{achievement.title}</div>
                                <div className="text-base md:text-lg text-gray-600">{achievement.description}</div>
                            </div>
                        </motion.div>
                    ))}

                    {achievements.length === 0 && (
                        <motion.div
                            className="text-gray-500 italic text-xl"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                        >
                            {t.noBadgesYet}
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AchievementsCard;
